import { useEffect, useState } from 'react';
import type { RoiId } from '~/domain/changes/ids';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { domainEventBus, RoiContoursModifiedEvent } from '~/domain/events';
import { comparablePair } from '~/state/atoms/structureSet/roiAtoms';

/**
 * Returns a counter that increments whenever one of the two compared ROIs has
 * its contours modified, so the comparison recomputes automatically with no
 * manual "Recompute" control.
 *
 * Every contour-changing operation — brush, draw, interpolation, boolean,
 * transforms, deform, and the SDF post-processing ops (smoothing, fill
 * cavities, remove small parts, margins) — completes through
 * `TextureWrite.finish()` / `RegionOfInterest.replaceTexture`, which publishes
 * a {@link RoiContoursModifiedEvent}. Listening to that single event therefore
 * covers them all. Events are also gated on the edit target's structure set,
 * the one the pair is compared in, so after a switch of the active set an
 * edit in the set left behind does not recompute. Dependents (the in-viewport overlay textures and
 * the metrics) list the returned value as an effect dependency.
 *
 * Stays at 0 while compare mode is inactive or the pair is not yet two
 * distinct ROIs.
 *
 * @param roiA ROI A (reference), or null.
 * @param roiB ROI B (test), or null.
 * @param active Whether compare mode is on.
 */
export function useComparisonEditToken(
    roiA: RoiId | null,
    roiB: RoiId | null,
    active: boolean
): number {
    const [editToken, setEditToken] = useState(0);
    const { structureSet } = useActiveEditTarget();
    const structureSetUid = structureSet?.dicomUid;

    useEffect(() => {
        if (!active) {
            return;
        }
        const pair = comparablePair(roiA, roiB);
        if (!pair) {
            return;
        }
        const [referenceRoi, testRoi] = pair;
        const subscription = domainEventBus.subscribe(
            RoiContoursModifiedEvent.EVENT_TYPE,
            (event: RoiContoursModifiedEvent) => {
                if (
                    structureSetUid !== undefined &&
                    event.structureSetUid !== structureSetUid
                ) {
                    return;
                }
                if (event.roiId === referenceRoi || event.roiId === testRoi) {
                    setEditToken((token) => token + 1);
                }
            }
        );
        return () => subscription.unsubscribe();
    }, [active, roiA, roiB, structureSetUid]);

    return editToken;
}
